/**
 * LoginOwnerPage - App Owner Portal Login.
 *
 * Two-step login:
 *   Step 1 — email + password (POST /owner/login).
 *   Step 2 — authenticator code OR recovery code (POST /owner/login/verify-2fa),
 *            shown only when the account has TOTP 2FA enabled.
 *
 * Route: /owner/login
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { AuthCard } from '../../components/auth/AuthCard';
import { AuthInput } from '../../components/auth/AuthInput';
import { AuthButton } from '../../components/auth/AuthButton';
import {
  MailIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  ChevronLeftIcon,
} from '../../components/auth/AuthIcons';

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
    'Something went wrong. Please try again.'
  );
}

export function LoginOwnerPage() {
  const navigate = useNavigate();
  const { loginOwner, verifyOwner2FA } = useAuth();
  const { showToast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [preAuthToken, setPreAuthToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const handleCredentials = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setLoading(true);
    setError(null);
    try {
      const result = await loginOwner({ email: email.trim().toLowerCase(), password });
      if (result.requires2FA && result.preAuthToken) {
        setPreAuthToken(result.preAuthToken);
        setCode('');
        setStep(2);
      } else {
        navigate('/owner');
      }
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.trim().length < 6 || !preAuthToken) return;
    setLoading(true);
    try {
      const { remainingRecoveryCodes } = await verifyOwner2FA(preAuthToken, code.trim());
      if (typeof remainingRecoveryCodes === 'number' && remainingRecoveryCodes <= 2) {
        showToast(
          'warning',
          `You signed in with a recovery code — only ${remainingRecoveryCodes} left. Regenerate them in Security.`,
        );
      }
      navigate('/owner');
    } catch (err) {
      showToast('error', extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep(1);
    setPreAuthToken(null);
    setCode('');
    setError(null);
  };

  return (
    <AuthLayout ownerMode>
      <AuthCard width={420}>
        {step === 1 ? (
          <>
            <div className="mb-7">
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3.5"
                style={{ background: 'linear-gradient(135deg, #1A1828, #241F3D)', border: '1px solid rgba(165,180,252,0.35)', boxShadow: '0 0 0 1px rgba(165,180,252,0.15), 0 6px 20px rgba(99,102,241,0.28)' }}
              >
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#A5B4FC' }} />
                <span className="text-[11px] font-bold tracking-[0.08em] uppercase" style={{ color: '#A5B4FC' }}>
                  Owner Portal
                </span>
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-[-0.03em] text-[var(--t1)] mb-1.5">
                Platform Access
              </h1>
              <p className="text-sm font-medium text-[var(--t3)]">
                Sign in to the App Owner dashboard
              </p>
            </div>

            <form onSubmit={handleCredentials} className="flex flex-col gap-[18px]">
              <AuthInput
                label="Owner Email"
                type="email"
                placeholder="owner@aisistoo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={<MailIcon />}
                autoComplete="email"
              />
              <AuthInput
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={<LockIcon />}
                rightIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                onRightIconClick={() => setShowPassword((p) => !p)}
                autoComplete="current-password"
              />
              {error && (
                <div
                  className="text-[13px] font-medium px-3 py-2 rounded-md"
                  style={{ color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA' }}
                  role="alert"
                >
                  {error}
                </div>
              )}
              <AuthButton type="submit" variant="owner" fullWidth loading={loading} size="lg">
                Sign In
              </AuthButton>
            </form>

            <p className="text-center text-xs font-medium text-[var(--t4)] mt-5">
              School staff?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-[var(--p)] font-bold bg-transparent border-none cursor-pointer hover:underline"
              >
                School Portal
              </button>
            </p>
          </>
        ) : (
          <>
            <div className="mb-7">
              <button
                type="button"
                onClick={goBack}
                className="flex items-center gap-1.5 bg-transparent border-none cursor-pointer text-[var(--t3)] text-[13px] font-semibold p-0 mb-4 hover:text-[var(--t2)]"
              >
                <ChevronLeftIcon />
                Back
              </button>
              <div
                className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-3.5"
                style={{ background: 'linear-gradient(135deg, var(--p), var(--p-dk))' }}
              >
                <LockIcon />
              </div>
              <h1 className="font-display text-[22px] font-extrabold tracking-[-0.03em] text-[var(--t1)] mb-1.5">
                Two-Factor Authentication
              </h1>
              <p className="text-[13px] font-medium text-[var(--t3)] leading-relaxed">
                Enter the 6-digit code from your authenticator app, or a recovery code.
              </p>
            </div>

            <input
              type="text"
              inputMode="text"
              autoFocus
              maxLength={9}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
              placeholder="6-digit code or recovery code"
              className="font-display"
              style={{ textAlign: 'center', letterSpacing: '0.3em', fontWeight: 700 }}
            />

            {error && (
              <div
                className="text-[13px] font-medium px-3 py-2 rounded-md mt-4"
                style={{ color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA' }}
                role="alert"
              >
                {error}
              </div>
            )}

            <div className="mt-5">
              <AuthButton
                variant="owner"
                fullWidth
                size="lg"
                loading={loading}
                onClick={handleVerify}
                disabled={code.trim().length < 6}
              >
                Verify and Sign In
              </AuthButton>
            </div>

            <p className="text-center text-xs font-medium text-[var(--t4)] mt-4 leading-relaxed">
              Lost access to your authenticator? Enter one of your recovery codes above instead.
            </p>
          </>
        )}
      </AuthCard>
    </AuthLayout>
  );
}

export default LoginOwnerPage;